import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, FileText } from "lucide-react";
import { apiClient } from "../lib/api";
import { trackPurchase } from "../lib/fbpixel";
import Header from "../components/Header";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const token = params.get("token");
  const nav = useNavigate();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let mounted = true;
    let n = 0;
    const poll = async () => {
      try {
        const r = await apiClient.get(`/payments/status/${sessionId}`);
        if (!mounted) return;
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          if (!firedRef.current) {
            firedRef.current = true;
            trackPurchase({
              value: Number(r.data.amount_total ?? r.data.amount ?? 0) || 0,
              currency: (r.data.currency || "EUR").toUpperCase(),
              content_name: "DoctorSol Dossier",
              content_category: "report",
            });
          }
        }
        else if (r.data.status === "expired") setStatus("expired");
        else if (n < 8) {
          n++; setAttempts(n);
          setTimeout(poll, 2000);
        } else setStatus("timeout");
      } catch {
        if (n < 8) { n++; setTimeout(poll, 2000); } else setStatus("error");
      }
    };
    poll();
    return () => { mounted = false; };
  }, [sessionId]);

  const downloadPDF = async () => {
    // We need lead_id; fetch via /leads/me using the token
    try {
      const r = await apiClient.get("/leads/me", { params: { token } });
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/report/${r.data.id}/pdf?token=${token}`, "_blank");
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <Header minimal />
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="payment-page">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-[#E78938] animate-spin mb-6" />
            <h1 className="serif text-4xl text-[#0F172A] mb-3">Confirmando tu pago…</h1>
            <p className="text-[#475569]">Intento {attempts + 1} de 8</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-[#E78938] mb-6" />
            <h1 className="serif text-5xl text-[#0F172A] mb-3">¡Pago confirmado!</h1>
            <p className="text-[#475569] mb-8">Tu Dossier Solar Personal está listo.</p>
            <button onClick={downloadPDF} data-testid="download-pdf-btn"
              className="bg-[#E78938] hover:bg-[#C5701D] text-white rounded-xl px-8 py-4 font-medium inline-flex items-center gap-2">
              <FileText className="w-5 h-5" /> Descargar PDF
            </button>
            <div className="mt-4">
              <button onClick={() => nav(`/portal?token=${token}`)} className="text-[#475569] hover:underline text-sm">
                Volver al portal
              </button>
            </div>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <h1 className="serif text-4xl text-[#0F172A] mb-3">No pudimos confirmar el pago</h1>
            <p className="text-[#475569] mb-6">Por favor, vuelve al portal e inténtalo de nuevo.</p>
            <button onClick={() => nav(`/portal?token=${token}`)}
              className="bg-[#0F172A] text-white rounded-xl px-6 py-3">Volver al portal</button>
          </>
        )}
      </div>
    </div>
  );
}
